import { LEAVE_TYPE_LABELS } from '@aegislab/shared';
import type {
  ApprovalKind,
  AttendanceStatus,
  ApprovalLineEntry,
  LeaveType,
  ProofStatus,
  Role,
  TeamOpsMember,
  TeamType,
  WorklogStatus,
} from '../model/teamOps';
import { getUtcDateString } from '@/shared/lib/format';

/**
 * 팀 운영 콘솔의 라벨과 날짜 표기 — 로스터·근태·워크로그 세 슬라이스와 위젯이 함께 쓴다.
 *
 * 날짜 계산은 전부 `T00:00:00Z` 를 붙인 UTC 고정 산술이다. bare `new Date(iso)` 는 로컬 파싱이라
 * 시간대에 따라 요일이 하루 밀린다(퀘스트 a13d46c2 CP3 이전부터의 규약).
 */
export const WEEKDAY_LABELS = ['월', '화', '수', '목', '금', '토', '일'];
/** ISO(YYYY-MM-DD) → 월요일 시작 요일 인덱스(0=월…6=일) — 주간 추이 '오늘' 칸 마커용.
   isoDaysBefore와 같은 UTC 고정 산술(타임존 무관) */
export function isoToMondayFirstWeekdayIndex(iso: string): number {
  return (new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7;
}

/** anchor(YYYY-MM-DD) 기준 N일 전 ISO — 처리내역 기간 프리셋용. UTC 고정 산술이라 타임존 무관 */
export function isoDaysBefore(anchorIso: string, days: number): string {
  const d = new Date(`${anchorIso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return getUtcDateString(d);
}
export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = LEAVE_TYPE_LABELS;

export const PROOF_STATUS_LABEL: Record<ProofStatus, string> = {
  PENDING: '증빙 대기',
  SUBMITTED: '제출 완료',
  REVIEWED: '확인 완료',
  OVERDUE: '미제출',
};

export const ROLE_LABEL: Record<Role, string> = {
  OPERATOR: '운영자',
  MANAGER: '매니저',
  PLAYER: '플레이어',
};

export const TEAM_TYPE_LABEL: Record<TeamType, string> = {
  DEPARTMENT: '부서',
  TASK_FORCE: 'TF',
};

/** 팀 수정 모달 타입 Select 옵션 — TEAM_TYPE_LABEL 파생 (실제 TeamModal과 동형) */
export const TEAM_TYPE_OPTIONS = (Object.keys(TEAM_TYPE_LABEL) as TeamType[]).map((value) => ({
  value,
  label: TEAM_TYPE_LABEL[value],
}));

export const WORKLOG_STATUS_LABEL: Record<WorklogStatus, string> = {
  SUBMITTED: '제출 완료',
  DRAFT: '임시저장',
  NONE: '미작성',
};

/**
 * 색 위계: 정상은 무채색, 주의만 색.
 * 제출 완료(정상)=neutral(plain) · 임시저장(주의)=warning · 미작성(주의)=warning.
 */
export const WORKLOG_STATUS_TONE: Record<WorklogStatus, 'neutral' | 'warning'> = {
  SUBMITTED: 'neutral',
  DRAFT: 'warning',
  NONE: 'warning',
};

/** ISO(YYYY-MM-DD) → 'MM.DD' 표시 — 최근 7일 리스트용 */
export function isoToMonthDay(iso: string): string {
  return iso.slice(5).replace('-', '.');
}

/** getUTCDay() 인덱스(0=일) → 한글 요일 — 비근무 날짜 그룹 소제목용 고정 매핑 */
export const SUNDAY_FIRST_WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

/** ISO(YYYY-MM-DD) → 'MM.DD (요일)' — isoDaysBefore와 같은 UTC 고정 산술(타임존 무관).
   bare `new Date(iso)` 로컬 파싱은 금지 — T00:00:00Z 명시로 요일 계산을 고정 */
export function isoToDayLabel(iso: string): string {
  const weekday = SUNDAY_FIRST_WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
  return `${isoToMonthDay(iso)} (${weekday})`;
}

/** 결재라인 상태(한글 도메인 값) → 배지 변형 키 — 승인=무채 채움 · 대기=외곽선 · 반려만 warning */
export const LINE_STATUS_VARIANT: Record<
  ApprovalLineEntry['status'],
  'approved' | 'waiting' | 'rejected'
> = {
  승인: 'approved',
  대기: 'waiting',
  반려: 'rejected',
};

/** 독려·경고 대상 적격 — 활성 계정이고 휴가가 아닌 구성원만. 레일 미작성 칩과 로스터 경고행의 공용 술어 */
export const isNudgeEligible = (m: TeamOpsMember) => m.active && m.attendance.status !== 'LEAVE';

/** TF 겸직(같은 사람의 팀별 복수 행)을 첫 등장 행으로 dedup — 사람 키 = userId(목업은 행 id 폴백).
   name 키는 동명이인을 병합해 모수를 왜곡하므로 금지. 헤드카운트·비율 모수를 행이 아닌 사람 유일로 센다 */
export function dedupByPerson(rows: TeamOpsMember[]): TeamOpsMember[] {
  const seen = new Set<string>();
  return rows.filter((m) => {
    const key = m.userId ?? m.id;
    return seen.has(key) ? false : (seen.add(key), true);
  });
}

export const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  PRESENT: '출근',
  ABSENT: '미출근',
  LEAVE: '휴가',
  EXCLUDED: '근태 집계 제외',
  UNKNOWN: '근태 미확인',
};

export const APPROVAL_KIND_LABEL: Record<ApprovalKind, string> = {
  LEAVE: '휴가',
  CORRECTION: '정정',
  OFFDAY: '비근무',
};
